import { motion, useScroll, useTransform } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { Header } from './HomePageLab';

const PREUVES = [
  {
    annee: '2011',
    tag: 'Genèse opérationnelle',
    lieu: 'Orléans',
    texte: "Déploiement initial auprès d'un public structurellement éloigné des dispositifs classiques. La méthodologie initie un mouvement propre : la mise en action précède la levée des freins.",
    source: 'Validation terrain',
  },
  {
    annee: '2011–2025',
    tag: "Épreuve de l'invariant",
    lieu: 'Contextes hybrides',
    texte: "Quinze années d'itération dans des environnements hétérogènes. La répétition des succès confirme l'existence d'un invariant : le mécanisme opère indépendamment des variables sociales ou sectorielles.",
    source: 'Étude empirique',
  },
  {
    annee: '2026',
    tag: 'Transcription numérique',
    lieu: 'Écosystème PESACORE',
    texte: "La méthodologie accède à sa pleine scalabilité. L'architecture numérique devient le garant de l'intégrité du protocole à grande échelle. La rigueur reste locale, la portée devient universelle.",
    source: 'Modèle plateforme',
  },
];

const APPLICATIONS = [
  { contexte: 'Établissement scolaire',   verbe: 'Éveiller la pensée critique'      },
  { contexte: 'Entreprise en transition', verbe: 'Transformer les comportements'    },
  { contexte: "Structure d'insertion",    verbe: 'Restaurer la projection'           },
  { contexte: 'Cabinet de conseil',       verbe: 'Modéliser la complexité'           },
  { contexte: 'Collectivité publique',    verbe: 'Engager durablement les équipes'  },
  { contexte: 'Parcours individuel',      verbe: 'Se réinventer avec méthodologie'  },
];

const CHOIX = [
  {
    numero: '01',
    titre: 'Ce qui se passe ici produit des effets là-bas',
    corps: "Chaque action dans la plateforme génère une conséquence dans la vie réelle — professionnelle, personnelle, ou organisationnelle. PESACORE n'est pas un environnement fermé sur lui-même. C'est un point de départ. L'engagement n'a de valeur que s'il laisse une trace au-dehors.",
    note: 'Ancrage dans le réel',
  },
  {
    numero: '02',
    titre: "Ce que vous avez accompli compte.",
    corps: "La progression dans PESACORE dépend exclusivement du chemin, de l'effort et de l'action — jamais du statut, du réseau, ou des ressources de départ. Le système de reconnaissance interne traduit cette logique en valeur tangible : chaque jalon validé compte, indépendamment du point de départ.",
    note: 'Reconnaissance structurée',
  },
  {
    numero: '03',
    titre: 'Un outil doit servir, plutôt que retenir.',
    corps: "PESACORE ne cherche pas à maximiser le temps passé sur la plateforme. Chaque parcours a une durée, chaque étape a une limite. Ce n'est pas une contrainte — c'est un choix de conception délibéré. Une plateforme utile rend l'utilisateur plus autonome, pas plus dépendant.",
    note: 'Design anti-addictif',
  },
  {
    numero: '04',
    titre: 'Progresser seul. Contribuer ensemble.',
    corps: "Chaque utilisateur avance à son rythme, dans un parcours qui lui appartient. Mais cette progression individuelle génère des ressources qu'il peut redistribuer — à un collègue, à un pair, à quelqu'un qui en a besoin. Ce n'est pas de la solidarité comme valeur déclarée.",
    note: 'Cercles vertueux',
  },
];

const PRINCIPES = [
  {
    titre: 'Mise en situation',
    corps: "On ne transmet pas un concept. On construit l'expérience qui le rend tangible. La compréhension qui s'ensuit n'est pas mémorisée — elle est vécue. C'est une différence de nature, pas de degré.",
  },
  {
    titre: 'Réflexivité',
    corps: "Chaque étape produit une trace — écrite, sonore, visuelle. Il ne s'agit pas d'un exercice de contrôle, c'est davantage la preuve que quelque chose s'est effectivement passé. On ne présume pas la transformation : on la documente.",
  },
  {
    titre: 'Scalabilité',
    corps: "La méthodologie fonctionne à un, à dix, à mille. Ce n'est pas une question d'adaptation : c'est une propriété intellectuelle. La plateforme en garantit la cohérence à toute échelle.",
  },
  {
    titre: 'Universalité',
    corps: "Le moteur logique est unifié, il équipe le dirigeant et l'étudiant, la grande organisation et l'individu en reconversion. En refusant de segmenter par niveau, par secteur ou par profil présumé, la méthodologie traite la capacité de Médiation comme une donnée universelle.",
  },
];

const Counter = ({ target, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const tick = (now) => {
          const p = Math.min((now - t0) / duration, 1);
          setCount(Math.floor((1 - Math.pow(1 - p, 3)) * target));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration]);
  return <span ref={ref}>{count}{suffix}</span>;
};

const styles = `
  .manifeste-page { font-family: 'Georgia', 'Times New Roman', serif; background: #f7f4ef; color: #1a1a1a; overflow-x: hidden; }
  .mono { font-family: monospace; }

  /* NAV */
  .manifeste-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid rgba(26,26,26,0.08);
    background: rgba(247,244,239,0.93);
    backdrop-filter: blur(14px);
  }
  @media (min-width: 640px) { .manifeste-nav { padding: 1.25rem 3rem; } }

  /* HERO */
  .hero-section {
    min-height: 100vh; display: flex; flex-direction: column;
    justify-content: flex-end;
    padding: 7rem 1.25rem 3rem;
  }
  @media (min-width: 640px) { .hero-section { padding: 9rem 3rem 5rem; } }

  .hero-colophon {
    display: none;
  }
  @media (min-width: 768px) {
    .hero-colophon {
      display: block;
      position: absolute; top: 9.5rem; right: 3rem;
      font-family: monospace; font-size: 0.55rem; letter-spacing: 0.35em;
      color: #d0ccc4; text-transform: uppercase; writing-mode: vertical-rl;
    }
  }

  .hero-h1 {
    font-size: clamp(2.2rem, 9vw, 7.5rem);
    font-weight: 400; line-height: 1.02; letter-spacing: -0.025em;
    margin: 0 0 2rem;
  }
  @media (min-width: 640px) { .hero-h1 { margin: 0 0 3.5rem; } }

  .hero-bottom {
    display: flex; flex-direction: column; gap: 2rem;
    border-top: 1px solid rgba(26,26,26,0.1); padding-top: 2rem;
  }
  @media (min-width: 768px) {
    .hero-bottom {
      display: grid; grid-template-columns: 1fr auto;
      gap: 5rem; align-items: end; padding-top: 2.5rem;
    }
  }

  .hero-cta-group { display: flex; flex-direction: column; gap: 0.5rem; }
  @media (min-width: 768px) { .hero-cta-group { align-items: flex-end; } }

  /* SECTION GÉNÉRIQUE */
  .section-pad { padding: 4rem 1.25rem; }
  @media (min-width: 640px) { .section-pad { padding: 8rem 3rem; } }
  .section-inner { max-width: 1200px; margin: 0 auto; }

  .section-dark { background: #111; color: #f7f4ef; padding: 4rem 1.25rem; }
  @media (min-width: 640px) { .section-dark { padding: 7rem 3rem; } }

  /* ARCHIVE */
  .archive-row {
    display: flex; flex-direction: column; gap: 1rem;
    padding: 2rem 0; border-top: 1px solid rgba(26,26,26,0.1);
  }
  @media (min-width: 640px) {
    .archive-row {
      display: grid; grid-template-columns: 110px 1fr;
      gap: 3.5rem; padding: 3rem 0;
    }
  }
  .archive-year {
    font-family: monospace; font-size: 2rem; font-weight: 300;
    line-height: 1; letter-spacing: -0.05em;
  }
  @media (min-width: 640px) { .archive-year { font-size: 2.4rem; } }

  /* CHIFFRES */
  .stats-grid {
    display: grid; grid-template-columns: 1fr;
    gap: 2.5rem;
    border-bottom: 1px solid rgba(247,244,239,0.08);
    padding-bottom: 3rem; margin-bottom: 3rem;
  }
  @media (min-width: 640px) {
    .stats-grid { grid-template-columns: repeat(3, 1fr); gap: 4rem; padding-bottom: 5rem; margin-bottom: 5rem; }
  }
  .stat-number { font-size: clamp(2.5rem, 8vw, 5.5rem); font-weight: 300; letter-spacing: -0.05em; line-height: 1; margin-bottom: 1rem; }

  /* UNIVERSALITÉ */
  .apps-grid {
    display: grid; grid-template-columns: 1fr;
  }
  @media (min-width: 480px) { .apps-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (min-width: 768px) { .apps-grid { grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); } }

  .app-card {
    padding: 1.75rem;
    border-top: 1px solid rgba(26,26,26,0.1);
  }
  @media (min-width: 480px) {
    .app-card:nth-child(odd) { border-right: 1px solid rgba(26,26,26,0.08); }
  }

  /* CHOIX */
  .choix-section { background: #141414; color: #f7f4ef; padding: 4rem 1.25rem; }
  @media (min-width: 640px) { .choix-section { padding: 8rem 3rem; } }

  .choix-grid {
    display: grid; grid-template-columns: 1fr;
    border-top: 1px solid rgba(247,244,239,0.07);
  }
  @media (min-width: 768px) { .choix-grid { grid-template-columns: repeat(2, 1fr); } }

  .choix-card {
    padding: 2rem 1.5rem;
    border-bottom: 1px solid rgba(247,244,239,0.07);
  }
  @media (min-width: 768px) {
    .choix-card { padding: 3rem; }
    .choix-card:nth-child(odd) { border-right: 1px solid rgba(247,244,239,0.07); }
  }

  /* PLATEFORME */
  .plateforme-section {
    background: #f0ece4; padding: 4rem 1.25rem;
    border-top: 1px solid rgba(26,26,26,0.07);
    border-bottom: 1px solid rgba(26,26,26,0.07);
  }
  @media (min-width: 640px) { .plateforme-section { padding: 8rem 3rem; } }

  .plateforme-grid {
    display: grid; grid-template-columns: 1fr; gap: 3rem;
  }
  @media (min-width: 1024px) {
    .plateforme-grid { grid-template-columns: 1fr 1fr; gap: 6rem; align-items: start; }
  }

  /* CTA FINAL */
  .cta-section { padding: 5rem 1.25rem; text-align: center; max-width: 900px; margin: 0 auto; }
  @media (min-width: 640px) { .cta-section { padding: 10rem 3rem; } }

  .cta-btns { display: flex; flex-direction: column; gap: 1rem; justify-content: center; }
  @media (min-width: 480px) { .cta-btns { flex-direction: row; flex-wrap: wrap; } }

  /* FOOTER */
  .manifeste-footer {
    padding: 1.5rem 1.25rem;
    border-top: 1px solid rgba(26,26,26,0.08);
    display: flex; flex-direction: column; gap: 0.75rem; align-items: center; text-align: center;
    font-family: monospace; font-size: 0.58rem; letter-spacing: 0.2em; color: #bbb; text-transform: uppercase;
  }
  @media (min-width: 640px) {
    .manifeste-footer { flex-direction: row; justify-content: space-between; padding: 2.25rem 3rem; }
  }

  .divider { height: 1px; background: linear-gradient(to right, transparent, rgba(26,26,26,0.13) 30%, rgba(26,26,26,0.13) 70%, transparent); margin: 0 1.25rem; }
  @media (min-width: 640px) { .divider { margin: 0 3rem; } }
`;



export default function ManifestePage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.65], [1, 0]);

  return (
    <div className="min-h-screen bg-[#020617] text-indigo-50 relative overflow-hidden" style={{ fontFamily: 'Georgia, serif' }}>

      {/* Fond : papier millimétré */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `
          linear-gradient(rgba(34,211,238,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(34,211,238,0.03) 1px, transparent 1px),
          linear-gradient(rgba(34,211,238,0.015) 1px, transparent 1px),
          linear-gradient(90deg, rgba(34,211,238,0.015) 1px, transparent 1px)
        `,
        backgroundSize: '80px 80px, 80px 80px, 16px 16px, 16px 16px',
      }} />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_30%_20%,rgba(34,211,238,0.07),transparent_55%)]" />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_70%_80%,rgba(245,158,11,0.04),transparent_55%)]" />

      <Header />

      {/* HERO */}
      <section ref={heroRef} className="relative pt-24 sm:pt-28 pb-12 sm:pb-24 px-4 sm:px-6 min-h-[auto] sm:min-h-[90vh] flex items-start sm:items-center">
        <div className="max-w-7xl mx-auto w-full">
          <div className="order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="flex flex-wrap items-center gap-2 sm:gap-3 mb-6 sm:mb-8"
            >
              <div className="h-px w-8 bg-indigo-200" />
              <span className="text-[9px] font-mono uppercase tracking-[0.25em] sm:tracking-[0.35em] text-indigo-200">
                PESACORE · Manifeste · 2026
              </span>
              <div className="flex items-center gap-1.5 ml-auto">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[9px] font-mono text-emerald-400/60 uppercase tracking-widest">Actif</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 sm:mb-6"
              style={{
                border: '1px solid rgba(34,211,238,0.12)',
                background: 'rgba(34,211,238,0.03)',
              }}
            >
              <span className="text-[10px] font-mono uppercase tracking-widest text-indigo-300/70">
                Plateforme de transformation
              </span>
            </motion.div>

            <div className="mb-5 sm:mb-6 overflow-hidden">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="font-black uppercase tracking-tight leading-[0.95] text-white"
                style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(2rem, 8vw, 3.75rem)' }}
              >
                La méthodologie
              </motion.h1>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <span
                  className="font-black italic tracking-tight leading-[0.95]"
                  style={{
                    fontSize: 'clamp(2rem, 8vw, 3.75rem)',
                    background: 'linear-gradient(135deg, #22d3ee 0%, #f59e0b 60%, #22d3ee 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                  précède la plateforme.
                </span>
              </motion.div>
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.7 }}
              className="text-indigo-100/60 leading-relaxed mb-2 max-w-lg"
              style={{ fontSize: 'clamp(0.82rem, 2.5vw, 1rem)' }}
            >
              PESACORE permet à une personne, une équipe ou une organisation de{' '}
              <span className="text-white font-semibold not-italic">changer de posture et de produire des résultats mesurables</span>
              {' '}— quelle que soit leur situation de départ.
              Une méthodologie éprouvée quinze ans sur le terrain, maintenant accessible à grande échelle.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-3 mb-6 sm:mb-8"
            >
              <a
                href="/auth/register"
                className="group relative flex items-center justify-center gap-3 px-7 py-3.5 overflow-hidden w-full sm:w-auto"
                style={{ background: 'rgba(245,158,11,0.12)', border: '1.5px solid rgba(245,158,11,0.4)' }}
              >
                <motion.span
                  className="absolute inset-0 opacity-0 group-hover:opacity-100"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(245,158,11,0.08), transparent)' }}
                  animate={{ x: ['-100%', '100%'] }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                />
                <span className="text-sm font-black uppercase tracking-wider text-white relative z-10">Entrer dans PESACORE →</span>
              </a>
              <span className="text-xs font-mono text-slate-300 flex items-center">Sans engagement · Sans carte bancaire</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* DIVIDER */}
      <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, rgba(34,211,238,0.15), transparent)' }} />

      {/* ARCHIVE */}
      <section className="py-12 sm:py-20 px-4 sm:px-6" style={{ borderBottom: '1px solid rgba(34,211,238,0.05)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <div className="text-[9px] font-mono text-indigo-100 uppercase tracking-[0.3em] mb-1">01 — L'Archive</div>
            <div className="flex items-end gap-6">
              <h2 className="font-black uppercase tracking-tight text-white leading-tight"
                style={{ fontSize: 'clamp(1.3rem, 5vw, 1.875rem)' }}>
                Une méthodologie d'abord constituée{' '}
                <span style={{ background: 'linear-gradient(90deg, #22d3ee, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  dans l'expérience concrète
                </span>
              </h2>
              <div className="h-px flex-1 bg-gradient-to-r from-indigo-500/20 to-transparent hidden md:block mb-2" />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-0">
            {PREUVES.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className="p-5 sm:p-8 relative group transition-all"
                style={{
                  background: 'rgba(2,6,23,0.0)',
                  borderTop: '1px solid rgba(34,211,238,0.07)',
                }}
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: 'rgba(34,211,238,0.015)' }} />

                <div className="text-[9px] font-mono text-indigo-300 mb-4 tracking-widest">{p.annee}</div>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="text-[9px] font-mono text-indigo-200 uppercase tracking-widest">{p.tag}</span>
                  <span className="text-[9px] font-mono text-slate-300 uppercase tracking-widest">{p.lieu}</span>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed relative z-10" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{p.texte}</p>
                <div className="text-[9px] font-mono text-slate-700 uppercase tracking-widest mt-3">Source · {p.source}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-12 sm:py-20 px-4 sm:px-6" style={{ borderTop: '1px solid rgba(34,211,238,0.05)', borderBottom: '1px solid rgba(34,211,238,0.05)', background: 'rgba(2,6,23,0.6)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { val: '50%', label: "de retour vers l'emploi ou la formation dès le premier projet", note: 'Résultat terrain · 2011' },
              { val: '15+', label: "années de mise en œuvre et d'ajustement avant toute formalisation logicielle", note: 'Méthodologie éprouvée' },
              { val: '100%', label: 'des secteurs concernés, sans adaptation de fond', note: 'Universalité structurelle' },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl sm:text-5xl font-black tracking-tight" style={{ background: 'linear-gradient(90deg, #22d3ee, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {s.val}
                </div>
                <p className="text-slate-400 text-sm leading-relaxed mt-2 max-w-xs mx-auto" style={{ fontStyle: 'italic' }}>{s.label}</p>
                <span className="text-[9px] font-mono text-slate-300 uppercase tracking-widest">{s.note}</span>
              </motion.div>
            ))}
          </div>

          <motion.blockquote
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-12 max-w-2xl mx-auto text-center"
            style={{ borderTop: '1px solid rgba(34,211,238,0.07)', paddingTop: '2rem' }}
          >
            <p className="text-lg sm:text-xl font-light text-indigo-100 leading-relaxed" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
              "La méthodologie n'appartient pas à un secteur. Elle appartient à quiconque saisit l'intention..."
            </p>
            <cite className="text-[9px] font-mono text-indigo-300 uppercase tracking-widest not-italic block mt-3">
              © Farid AIT-MAHREZ (Directeur général)
            </cite>
          </motion.blockquote>
        </div>
      </section>

      {/* UNIVERSALITÉ */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <div className="text-[9px] font-mono text-indigo-100 uppercase tracking-[0.3em] mb-1">02 — L'Universalité</div>
            <div className="flex items-end gap-6">
              <h2 className="font-black uppercase tracking-tight text-white leading-tight"
                style={{ fontSize: 'clamp(1.3rem, 5vw, 1.875rem)' }}>
                Ni secteur privilégié, ni public spécifique.
                Une <span style={{ background: 'linear-gradient(90deg, #22d3ee, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  logique
                </span> opératoire.
              </h2>
              <div className="h-px flex-1 bg-gradient-to-r from-indigo-500/20 to-transparent hidden md:block mb-2" />
            </div>
          </div>

          <p className="text-slate-400 text-sm leading-relaxed max-w-3xl mb-8" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
            Une méthodologie réellement universelle ne requiert pas de déclinaisons multiples adaptées à chaque contexte. Elle dégage le mécanisme invariant présent dans toute transformation humaine sérieuse, puis l'applique avec constance quel que soit le domaine ou l'interlocuteur.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {APPLICATIONS.map((app, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="p-5 group relative transition-all"
                style={{ border: '1px solid rgba(34,211,238,0.06)', background: 'rgba(2,6,23,0.4)' }}
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: 'rgba(34,211,238,0.015)' }} />
                <div className="text-[9px] font-mono text-indigo-500/30 mb-2">{String(i + 1).padStart(2, '0')} ·</div>
                <div className="text-slate-200 text-xs font-mono uppercase tracking-widest mb-1">{app.contexte}</div>
                <div className="text-white text-base font-semibold">{app.verbe}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* QUATRE CHOIX */}
      <section className="py-12 sm:py-20 px-4 sm:px-6" style={{ borderTop: '1px solid rgba(34,211,238,0.05)', borderBottom: '1px solid rgba(34,211,238,0.05)', background: 'rgba(2,6,23,0.6)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <div className="text-[9px] font-mono text-indigo-100 uppercase tracking-[0.3em] mb-1">03 — Quatre choix de conception</div>
            <div className="flex items-end gap-6">
              <h2 className="font-black uppercase tracking-tight text-white leading-tight"
                style={{ fontSize: 'clamp(1.3rem, 5vw, 1.875rem)' }}>
                Ce que PESACORE a choisi de faire —
                et ce qu'il a choisi de{' '}
                <span style={{ background: 'linear-gradient(90deg, #f97316, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  ne pas faire
                </span>
              </h2>
              <div className="h-px flex-1 bg-gradient-to-r from-indigo-500/20 to-transparent hidden md:block mb-2" />
            </div>
            <p className="text-slate-200 text-sm max-w-2xl mt-3" style={{ fontStyle: 'italic' }}>
              Ces choix ne sont pas des contraintes réglementaires ou des options marketing. Ce sont des décisions de conception — délibérées, au détriment de ce qui aurait été plus simple ou plus rentable à court terme.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {CHOIX.map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-5 group relative transition-all"
                style={{ border: '1px solid rgba(34,211,238,0.06)', background: 'rgba(2,6,23,0.4)' }}
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: 'rgba(34,211,238,0.015)' }} />
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[9px] font-mono text-indigo-100">{c.numero} ·</span>
                  <span className="text-[8px] font-mono text-slate-300 uppercase tracking-widest">{c.note}</span>
                </div>
                <h3 className="text-white font-semibold text-base mb-2">{c.titre}</h3>
                <p className="text-slate-400 text-sm leading-relaxed" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{c.corps}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LA PLATEFORME */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-14">
            <motion.div initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="text-[9px] font-mono text-amber-500/50 uppercase tracking-[0.3em] mb-2">04 — La Plateforme</div>
              <h2 className="font-black uppercase tracking-tight mb-4 text-white leading-none"
                style={{ fontSize: 'clamp(1.5rem, 5vw, 2.25rem)' }}>
                PESACORE n'est pas un système de formation de plus.
                <br />
                <span className="italic font-normal" style={{
                  fontSize: 'clamp(1.2rem, 4vw, 1.875rem)',
                  background: 'linear-gradient(90deg, #22d3ee, #f59e0b)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  C'est la méthodologie, rendue accessible.
                </span>
              </h2>
              <p className="text-slate-300 leading-relaxed mb-4 text-sm" style={{ fontStyle: 'italic' }}>
                La plupart des plateformes d'apprentissage construisent d'abord la technologie, puis cherchent la pédagogie qui lui correspondrait. PESACORE suit la logique inverse. La méthodologie existait. Elle était éprouvée. La plateforme lui a donné une portée.
              </p>
              <p className="text-slate-400 text-sm leading-relaxed mb-6" style={{ fontStyle: 'italic' }}>
                Ce que vous trouvez ici n'est pas une suite d'outils à assembler. C'est un système conçu pour que la personne ou l'organisation face à vous s'engage dans un processus réflexif, où l'engagement est soutenu de manière mesurable et durable.
              </p>
              <a
                href="/auth/register"
                className="inline-flex items-center gap-2 px-6 py-3 text-white text-sm font-mono uppercase tracking-wider transition-all"
                style={{ border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.06)' }}
                onMouseEnter={e => { e.currentTarget.style.border = '1px solid rgba(245,158,11,0.5)'; e.currentTarget.style.background = 'rgba(245,158,11,0.10)'; }}
                onMouseLeave={e => { e.currentTarget.style.border = '1px solid rgba(245,158,11,0.3)'; e.currentTarget.style.background = 'rgba(245,158,11,0.06)'; }}
              >
                Accéder à la plateforme →
              </a>
            </motion.div>

            <div>
              {PRINCIPES.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="py-4 group relative"
                  style={{ borderBottom: i < PRINCIPES.length - 1 ? '1px solid rgba(34,211,238,0.05)' : 'none' }}
                >
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-[9px] font-mono text-indigo-100">{String(i + 1).padStart(2, '0')}</span>
                    <h3 className="text-white font-semibold text-sm">{item.titre}</h3>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed pl-6" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{item.corps}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-12 sm:py-20 px-4 sm:px-6" style={{ borderTop: '1px solid rgba(34,211,238,0.05)', background: 'rgba(2,6,23,0.6)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="relative p-8 sm:p-14 overflow-hidden" style={{
            background: 'rgba(2,6,23,0.8)',
            border: '1px solid rgba(34,211,238,0.07)',
          }}>
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(34,211,238,0.05),transparent_65%)] pointer-events-none" />
            <span className="absolute top-0 left-0 w-6 h-6 border-t border-l border-indigo-500/20" />
            <span className="absolute top-0 right-0 w-6 h-6 border-t border-r border-indigo-500/20" />
            <span className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-amber-500/15" />
            <span className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-amber-500/15" />

            <div className="text-[9px] font-mono text-indigo-100 uppercase tracking-[0.3em] mb-4">Ce n'est pas une démonstration. C'est une invitation.</div>

            <h2 className="font-black uppercase tracking-tight text-white mb-4 relative z-10"
              style={{ fontSize: 'clamp(1.3rem, 5vw, 1.875rem)' }}>
              Quel changement
              <br />
              <span style={{ background: 'linear-gradient(90deg, #22d3ee, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                voulez-vous produire ?
              </span>
            </h2>
            <p className="text-slate-400 mb-8 sm:mb-10 relative z-10 max-w-md mx-auto text-sm leading-relaxed" style={{ fontStyle: 'italic' }}>
              Certains arrivent avec un projet précis. D'autres avec une direction à préciser. La méthodologie fonctionne dans les deux cas.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto relative z-10 justify-center">
              <a
                href="/auth/register"
                className="px-7 py-3.5 font-black uppercase text-white text-sm tracking-wider transition-all text-center"
                style={{ border: '1px solid rgba(245,158,11,0.35)', background: 'rgba(245,158,11,0.07)' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(245,158,11,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(245,158,11,0.07)'; }}
              >
                Essai gratuit
              </a>
              <a
                href="/contact"
                className="px-7 py-3.5 font-black uppercase text-white text-sm tracking-wider transition-all text-center"
                style={{ border: '1px solid rgba(34,211,238,0.2)', background: 'rgba(34,211,238,0.03)' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(34,211,238,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(34,211,238,0.03)'; }}
              >
                Discuter du projet
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-8 px-6 text-center text-xs text-white font-mono uppercase tracking-widest"
        style={{ borderTop: '1px solid rgba(34,211,238,0.05)', background: 'rgba(2,6,23,0.5)' }}>
        © 2026 · PESACORE Architect : Tous droits réservés.
      </footer>
    </div>
  );
}